<!--@format-->
<template>
    <div class="widget psurvey-publish-widget">
        <div class="widget-header">
            <h3>Publish to Partners</h3>
            <div class="svg-container widget-close-button" @click="closeWidget">
                <widget-close-svg />
            </div>
        </div>
        <div class="content-wrapper">
            <p>
                Please select which partners you would like this partner survey to be published to:
            </p>
            <selector-table
                v-model="selectedClients"
                hide-headers
                item-container-title="Selected Partners"
                is-chip-display
                is-multiselect
                :is-initializing="isLoading"
                :items="availableClients"
                :selected-items="selectedClients"
                :table-config="tableConfig"
                @removed-item="handleRemovedItem"
                @remove-all="handleRemoveAll"
                @selected-item="handleSelectedItem"
                @toggle-select-all="handleToggleSelectAllItems"
                @query="handleQuery"
            />
        </div>
        <div class="widget-footer">
            <div class="cancel-container">
                <app-button class="transparent-btn" @click="closeWidget">Cancel</app-button>
            </div>
            <div class="action-container">
                <app-button :loading="isLoading" @click="save">
                    {{ isLoading ? 'Saving' : 'Save' }}
                </app-button>
            </div>
        </div>
    </div>
</template>

<script>
import EventBus from '@/scripts/event-bus.js';
import { PartnerSurveyService } from '@/scripts/services/partner-survey/partner-survey-service';
import { LogLevel } from '@/scripts/logger';

const AppButton = () => import('@/components/common/app-button');
const WidgetCloseSvg = () => import('@/components/svg/quick-add/widget-close-svg');
const SelectorTable = () => import('@/components/selector-table/selector-table');

export default {
    name: 'psurvey-publish-widget',

    components: {
        'app-button': AppButton,
        'selector-table': SelectorTable,
        'widget-close-svg': WidgetCloseSvg,
    },

    props: {
        widget: {
            type: Object,
            required: true,
        },
    },

    data() {
        return {
            isLoading: false,

            clients: [],

            selectedClients: [],

            failedToLoadSelectedClients: false,

            tableConfig: [
                {
                    cellWidth: '570px',
                    id: 1,
                    label: 'Name',
                    type: 'name',
                    customCellStyles: {
                        padding: '0 8px',
                    },
                },
            ],
        };
    },

    computed: {
        agencyId() {
            return this.$store.state.context.agency.id;
        },

        availableClients() {
            return this.clients.reduce((acc, client) => {
                if (
                    !(
                        this.selectedClients.length > 0 &&
                        this.selectedClients.some(({ id }) => id === client.id)
                    )
                ) {
                    acc.push(client);
                }
                return acc;
            }, []);
        },

        accountId() {
            return this.widget.accountId;
        },

        templateId() {
            return this.widget.partnerSurveyId;
        },
    },

    created() {
        this.isLoading = true;
        this.getData()
            .then(this.getInitialSelected)
            .then(() => (this.isLoading = false));
    },

    methods: {
        handleQuery(options) {
            this.isLoading = true;
            this.getData(options).then(() => (this.isLoading = false));
        },

        mapItems(item) {
            return {
                id: item.Id,
                name: item.Name,
                thumbnailImageUrl: item.LogoImageUrl,
            };
        },

        getData(options = {}) {
            return new Promise((resolve, reject) => {
                const config = {
                    filter: `AgencyClients/any(ac: ac/AgencyId eq ${this.agencyId})`,
                    select: 'Id,Name,LogoImageUrl',
                };

                if (options.search) {
                    config.filter = `${config.filter} and contains(Name, '${options.search}')`;
                }

                PartnerSurveyService.getClients(config)
                    .then(({ data: { value } }) => {
                        this.clients = value.map(this.mapItems);
                        resolve();
                    })
                    .catch((error) => {
                        this.$helpers.showFailNotification(
                            'An error occurred while fetching your partners.',
                            { error },
                        );
                        reject();
                    });
            });
        },

        getInitialSelected() {
            return new Promise((resolve, reject) => {
                PartnerSurveyService.getSharedAssets({
                    id: this.templateId,
                    accountId: this.accountId,
                })
                    .then(({ data: { data } }) => {
                        this.selectedClients = data.data
                            .map(({ Id }) => {
                                const selectedClient = this.clients.find(({ id }) => id === Id);
                                if (selectedClient) {
                                    return selectedClient;
                                } else {
                                    this.failedToLoadSelectedClients = true;
                                    return false;
                                }
                            })
                            .filter(Boolean);
                        resolve();
                    })
                    .then(() => {
                        if (this.failedToLoadSelectedClients) {
                            this.$helpers.showFailNotification(
                                "The Marketing Coordinator role is required to manage this survey's participants. Please contact your Account Manager for assistance.",
                                { level: LogLevel.INFO },
                            );
                        }
                    })
                    .catch((error) => {
                        this.$helpers.showFailNotification(
                            'An error occurred while fetching your partners.',
                            { error },
                        );
                        this.failedToLoadSelectedClients = true;
                        this.isLoading = false;
                        reject();
                    });
            });
        },

        handleRemoveAll() {
            this.selectedClients.splice(0, this.selectedClients.length);
        },

        handleRemovedItem(item) {
            this.selectedClients = this.selectedClients.filter(({ id }) => id !== item.id);
        },

        handleSelectedItem(item) {
            if (this.selectedClients.every(({ id }) => id !== item.id)) {
                this.selectedClients.push(item);
            }
        },

        handleToggleSelectAllItems(items) {
            if (this.clients.length === 0) {
                return (this.selectedClients = []);
            }
            items.map(this.handleSelectedItem);
        },

        save() {
            if (this.failedToLoadSelectedClients) {
                this.$helpers.showFailNotification(
                    'The Marketing Coordinator role is required to publish this survey. Please contact your Account Manager for assistance.',
                    { level: LogLevel.INFO },
                );
                return; // If we saved after failing to load all selected clients, the missing ones would be deleted.
            }

            this.isLoading = true;
            const clientIds = this.selectedClients.map(({ id }) => id);
            const payload = {
                SurveyId: this.templateId,
                ClientIds: clientIds,
            };

            PartnerSurveyService.publish({ accountId: this.accountId, payload })
                .then(() => {
                    this.$helpers.showSuccessNotification(
                        'Your publish settings have been updated.',
                    );

                    EventBus.$emit('partner-survey:update');
                    this.closeWidget();
                })
                .catch((error) => {
                    this.$helpers.showFailNotification(
                        'An error occurred while saving your partner survey publish settings.',
                        { error },
                    );
                })
                .finally(() => {
                    this.isLoading = false;
                });
        },

        closeWidget() {
            EventBus.$emit('close-widget', this.widget);
        },
    },
};
</script>

<style>
.psurvey-publish-widget .saving-step {
    text-align: center;
}

.psurvey-publish-widget .content-wrapper {
    padding: 20px;
}

.psurvey-publish-widget .content-wrapper .items-table .app-logo .background-image {
    border-radius: 50%;
}

.psurvey-publish-widget .action-container {
    display: flex;
}

.psurvey-publish-widget .action-container .btn {
    margin-left: 15px;
}

.psurvey-publish-widget .cancel-container .btn.transparent-btn {
    background: transparent;
    color: #877d88;
}

.psurvey-publish-widget .cancel-container .btn.transparent-btn:hover {
    background: #e1e0e5;
}

.psurvey-publish-widget .action-container .btn.white-btn {
    background: #ffffff;
    color: #877d88;
    border: 2px solid #e9e9ef;
}
</style>
